import { Inject, Injectable } from '@angular/core';
import { TrafficTypeEnum } from '../../models/cartDetails/trafficType.model';
import { PrimeAddonsService } from './prime-addons.service';
import { CommonAddonsService } from './common-addons.service';
import { IServerResponse } from '../../interfaces/server-response.interface';
import { CommonModalService } from '../../utils/modals/common-modal.service';
import {
  NotificationProviderService,
  NotificationProviderTypeEnum
} from '../../external-interfaces/notification-provider.service';
import { IAddon } from '../../interfaces/cart-details.interface';
import { CommonModalTemplatesEnum } from '../../utils/modals/common-modal-templates.enum';

@Injectable()
export class SlotAddonsService {
  constructor(
    private primeAddonsService: PrimeAddonsService,
    private commonAddonsService: CommonAddonsService,
    private commonModalService: CommonModalService,
    private notificationProvider: NotificationProviderService,
    @Inject('Endpoint') private endpoint: any,
  ) {
  }

  public showModal(slot: any): void {
    this.prepareTempState(slot);

    this.primeAddonsService.resetAddons(slot);
    this.primeAddonsService.updateAll(slot);

    const modalData = {
      templateName: CommonModalTemplatesEnum.ADDONS_MODAL,
      isLoaderVisible: false,
      expanded: false,
      msisdn: slot.getMsisdn(),
      showHeader: slot.trafficType !== TrafficTypeEnum.TV,
      optionalAddons: slot._computedFields.visibleOptionalAddons,
      groupedAddon: slot._computedFields.groupedAddon,

      updateSingleSelection: (addon: IAddon) => {
        this.primeAddonsService.singleUpdate(addon, slot);
      },

      updateGroupSelection: () => {
        this.primeAddonsService.groupUpdate(slot);
      },

      updateGroupSelectionFromKeyboard: (readOnly: boolean, addonCode: string) => {
        if (readOnly) {
          return;
        } else {
          modalData.updateGroupSelection();
          modalData.groupedAddon._computedFields.tempSelectedSubAddonCode = addonCode;
        }
      },

      save: (event: Event) => {
        event.preventDefault();

        const oldSelectedVisibleOptionalAddonsCodes = slot._computedFields.selectedVisibleOptionalAddonsCodes;

        const newSelectedVisibleOptionalAddonsCodes = slot._computedFields.visibleOptionalAddons
          .filter((addon: any) => addon._computedFields.tempSelected)
          .map((addon: any) => addon.addonCode);

        const addonsToAdd = newSelectedVisibleOptionalAddonsCodes
          .filter((addonCode: any) => !oldSelectedVisibleOptionalAddonsCodes.includes(addonCode));

        const addonsToDelete = oldSelectedVisibleOptionalAddonsCodes
          .filter((addonCode: any) => !newSelectedVisibleOptionalAddonsCodes.includes(addonCode));

        const addonGroup = slot._computedFields.groupedAddon;

        if (addonGroup) {
          const oldGroupedAddonSelected: string = addonGroup.selected;
          const newGroupedAddonSelected: string = addonGroup._computedFields.tempSelected;
          const oldSelectedSubAddonCode: string = addonGroup._computedFields.selectedSubAddonCode;
          const newSelectedSubAddonCode: string = addonGroup._computedFields.tempSelectedSubAddonCode;
          let isPossibleToAddNewAddon = true;

          addonGroup.groupedAddons.forEach((primeAddon: IAddon) => {
            if (newSelectedSubAddonCode === primeAddon.addonCode) {
              isPossibleToAddNewAddon = !primeAddon._computedFields.readOnly;
            }
          });

          // add selected grouped addon code to addonsToDelete
          if (oldGroupedAddonSelected
              && (!newGroupedAddonSelected || oldSelectedSubAddonCode !== newSelectedSubAddonCode)) {
            addonsToDelete.push(oldSelectedSubAddonCode);
          }

          // add selected grouped addon code to addonsToAdd
          if (isPossibleToAddNewAddon && newGroupedAddonSelected
              && (!oldGroupedAddonSelected || oldSelectedSubAddonCode !== newSelectedSubAddonCode)) {
            addonsToAdd.push(newSelectedSubAddonCode)
          }
        }

        if (!addonsToAdd.length && !addonsToDelete.length) {
          this.commonModalService.removeModal(CommonModalTemplatesEnum.ADDONS_MODAL);

          return;
        }

        this.commonAddonsService.setSlotAddonsToAdd(addonsToAdd, slot.bundleNumber);

        modalData.isLoaderVisible = true;

        this.endpoint('addons.update', {
          addonsToAdd,
          addonsToDelete,
          bundleNo: slot.bundleNumber
        }).queue('addons')
          .run()
          .then((endpointResponse: IServerResponse) => {
            this.handleAddonUpdate(endpointResponse, addonsToAdd, addonsToDelete, slot.ordinal);
          })
          .finally(() => {
            modalData.isLoaderVisible = false;
          });
      }
    };

    this.commonModalService.setData(modalData);
  }

  private prepareTempState(slot: any): void {
    slot._computedFields.visibleOptionalAddons.forEach((addon: any) => {
    return addon._computedFields.tempSelected = addon.selected;
  });

    if (slot._computedFields.groupedAddon) {
      slot._computedFields.groupedAddon._computedFields.tempSelected = slot._computedFields.groupedAddon.selected;

      slot._computedFields.groupedAddon._computedFields.tempSelectedSubAddonCode = slot._computedFields.groupedAddon._computedFields.selectedSubAddonCode;
      }
  }

  private handleAddonUpdate(response: any, addonsToAdd: any, addonsToDelete: any, slotOrdinal: any): void {
    addonsToDelete.forEach((addonCode: any) => this.commonAddonsService.slideAddon(slotOrdinal, addonCode, 'slideUp'));

    setTimeout(() => {
      this.notificationProvider.publish(
        NotificationProviderTypeEnum.CART_DETAILS,
        response
      );

      setTimeout(() => {
        addonsToAdd.forEach((addonCode: any) => this.commonAddonsService.slideAddon(slotOrdinal, addonCode, 'slideDown'));
      });

      this.commonModalService.removeModal(CommonModalTemplatesEnum.ADDONS_MODAL);

    }, this.commonAddonsService.ADDON_ANIMATION_DURATION);
  }
}
